import moment from "moment";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  onFilterMenuCategory,
  onFilterMenuCombo,
  onFilterLoyaltyProducts,
  onSearchMenu,
} from "../store/Menu/MenuActions";
import { convertToMoment } from "../utils/TimeUtils";

const useSearchHook = () => {
  const dispatch = useDispatch();
  const { searchPhrase, category, comboModified, loyaltyMenu } = useSelector(
    (state) => state.menu
  );
  const { data: cartData } = useSelector((state) => state.cart);
  
  // Refs to track cart length and previous route
  const previousCartLength = useRef(cartData?.length || 0);
  const previousRoute = useRef(window.location.pathname);
  const lastSearchClearTime = useRef(0); // Throttle search clearing

  // ✅ Clear search when route changes - Fixed to prevent render-time dispatch
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (previousRoute.current !== currentPath && searchPhrase && searchPhrase.trim() !== "") {
      // Use setTimeout to defer dispatch to next tick, avoiding render-time dispatch
      setTimeout(() => {
        dispatch(onSearchMenu(""));
      }, 0);
    }
    previousRoute.current = currentPath;
  }, [searchPhrase, dispatch]);

  // Monitor route changes with interval (since we can't use router hooks) - Optimized
  useEffect(() => {
    // Only set up interval if search is active to reduce unnecessary checks
    if (!searchPhrase || searchPhrase.trim() === "") {
      return;
    }

    const checkRouteChange = () => {
      const currentPath = window.location.pathname;
      if (previousRoute.current !== currentPath) {
        // Use requestAnimationFrame for better performance
        requestAnimationFrame(() => {
          dispatch(onSearchMenu(""));
        });
        previousRoute.current = currentPath;
      }
    };

    const interval = setInterval(checkRouteChange, 1000); // Reduced frequency to 1000ms
    return () => clearInterval(interval);
  }, [searchPhrase, dispatch]);

  // ✅ Clear search when item is added to cart - Optimized with throttling
  useEffect(() => {
    const currentCartLength = cartData?.length || 0;
    const now = Date.now();
    
    // Only process if cart length actually changed and search is active
    if (currentCartLength !== previousCartLength.current && searchPhrase && searchPhrase.trim() !== "") {
      // Only clear search if cart length increased (item added), not decreased (item removed)
      if (currentCartLength > previousCartLength.current) {
        // Throttle to prevent excessive clearing (max once per 500ms)
        if (now - lastSearchClearTime.current > 500) {
          lastSearchClearTime.current = now;
          // Use requestAnimationFrame for better performance than setTimeout
          requestAnimationFrame(() => {
            dispatch(onSearchMenu(""));
          });
        }
      }
    }
    
    // Update previous cart length
    previousCartLength.current = currentCartLength;
  }, [cartData?.length, searchPhrase, dispatch]);

  // ✅ Debounce search for better performance - Optimized to batch dispatches
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      // Use requestAnimationFrame to batch all dispatches together
      requestAnimationFrame(() => {
        const catSearc = handleCategorySearch(searchPhrase);
        const comSearch = handleComboSearch(searchPhrase);
        const loyaltySearch = handleLoyaltyProductsSearch(searchPhrase);
        
        // Batch all dispatches in a single frame
        dispatch(onFilterMenuCategory(catSearc));
        dispatch(onFilterMenuCombo(comSearch));
        dispatch(onFilterLoyaltyProducts(loyaltySearch));
      });
    }, 300); // ✅ 300ms debounce

    return () => clearTimeout(debounceTimeout);
  }, [searchPhrase]);

  const checkItemAvailability = (item) => {
    if (!item?.catAvailability || item?.catAvailability?.length === 0) {
      return true;
    }

    const availability = item?.catAvailability?.filter(
      (a) => a.day === moment().day()
    );
    if (availability.length === 0) {
      return false;
    }

    for (let i = 0; i < availability.length; i++) {
      if (item?.show_only === 1) {
        const start = convertToMoment(availability[i]?.start);
        const stop = convertToMoment(availability[i]?.end);
        if (!moment().isBetween(start, stop)) {
          return false;
        }
      }
    }

    return true;
  };

  const handleCategorySearch = (searchKey) => {
    if (searchKey) {
      const _category = [...category];
      const res = _category.map((_c) => ({
        ..._c,
        item: _c?.item?.filter(
          (i) => i.label.toLowerCase().indexOf(searchKey.toLowerCase()) > -1
        ),
      }));
      const cat = res?.filter((c) => checkItemAvailability(c));
      return cat || res;
    } else {
      const _category = [...category];
      const res = _category?.filter((c) => checkItemAvailability(c));
      return res || _category;
    }
  };

  const handleComboSearch = (searchKey) => {
    if (searchPhrase) {
      let res = {};
      //combo_name filter
      if (comboModified) {
        Object.keys(comboModified).forEach(
          (c) =>
            (res = {
              ...res,
              [c]: comboModified[c]?.filter(
                (cm) =>
                  cm?.combo_name
                    .toLowerCase()
                    .indexOf(searchKey.toLowerCase()) > -1
              ),
            })
        );
      }
      return res;
    } else {
      return comboModified;
    }
  };

  const handleLoyaltyProductsSearch = (searchKey) => {
    if (searchKey && loyaltyMenu?.eligible_products) {
      // Filter loyalty products based on product name/label
      const filteredProducts = loyaltyMenu.eligible_products.filter(
        (product) =>
          product?.label?.toLowerCase().indexOf(searchKey.toLowerCase()) > -1
      );
      return filteredProducts;
    } else {
      // Return all eligible products when no search term
      return loyaltyMenu?.eligible_products || [];
    }
  };

  return { handleCategorySearch, handleComboSearch, handleLoyaltyProductsSearch };
};

export default useSearchHook;
